import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCache, getOrBuildSelector } from '../../Sinks/Selector/SelectorCache.ts'
import { selectFilteredCube } from '../../Sinks/Selector/SelectFilteredCube.ts'
import { forwardCubeStatus, selectCubeContext } from '../../Sinks/Selector/CubeContext.ts'
import type { KronaTraitFilterOpts, TransformResult } from '../Types.ts'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { NO_TRAIT } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import type { TaxaAggregationResult } from '../../Sinks/Selector/TaxaAggregation.ts'
import { buildTraitsByTaxonMap, normTaxon } from './TraitCommon.ts'

const selectorCache = createSelectorCache<TransformResult<TaxaAggregationResult>>()

/**
 * Trait composition for ONE bucket — the single-bucket counterpart of `traitStackedBarSeries`,
 * shaped like `taxaAggregationSingle` ({ taxa: traitNames, rows: [{ bucketKey, bucketLabel, data }] })
 * so Pie/Treemap render it unchanged. Trait-join semantics mirror `traitStackedBarSeries`: a row's
 * lineage taxa map to trait names of the selected group; rows with no matching trait fall into
 * `NO_TRAIT` unless the source's `noTraitMode` excludes them.
 */
export function selectTraitAggregationSingle(
  state: typeof State,
  sourceId: SourceId,
  bucketKey: string | undefined,
  opts: KronaTraitFilterOpts,
  optsKey: string
): TransformResult<TaxaAggregationResult> {
  const cacheId = `${sourceId}|${bucketKey}|${optsKey}`

  return getOrBuildSelector(selectorCache, cacheId, () =>
    createSelector(
      [
        (s: typeof State) => selectCubeContext(s, sourceId),
        (s: typeof State) =>
          selectFilteredCube(s, sourceId, { selectedPath: opts.selectedPath, gradeThreshold: opts.gradeThreshold }),
        (s: typeof State) => s.visualization.traits?.traits ?? [],
        (s: typeof State) => s.visualization.sources[sourceId]?.shared?.natureKPIs
      ],
      (ctx, filtered, allTraits, natureKPIs): TransformResult<TaxaAggregationResult> => {
        if (ctx.status !== 'ready') return forwardCubeStatus(ctx)

        const bucket = filtered.find((b) => b.bucketKey === bucketKey) ?? {
          bucketKey: bucketKey ?? '',
          bucketLabel: bucketKey ?? '',
          values: []
        }

        const traitsByTaxon = buildTraitsByTaxonMap(allTraits, opts.traitGroup)
        const excludeNoTrait = (natureKPIs?.noTraitMode ?? 'include') === 'exclude'
        const agg: Record<string, number> = {}

        for (const v of bucket.values) {
          const mag = v.magnitude ?? 0
          if (!mag) continue

          const traitNamesForRow = new Set<string>()
          for (const name of v.taxa) {
            const list = traitsByTaxon.get(normTaxon(name ?? ''))
            if (list) for (const tr of list) traitNamesForRow.add(tr)
          }

          if (traitNamesForRow.size === 0) {
            if (!excludeNoTrait) agg[NO_TRAIT] = (agg[NO_TRAIT] ?? 0) + mag
          } else {
            for (const tr of traitNamesForRow) agg[tr] = (agg[tr] ?? 0) + mag
          }
        }

        return {
          status: 'ready',
          value: { taxa: Object.keys(agg), rows: [{ bucketKey: bucket.bucketKey, bucketLabel: bucket.bucketLabel, data: agg }] }
        }
      }
    )
  )(state)
}
